// pages/players/tabs/PlayerDetailsTab.jsx
import { useEffect, useState } from "react";
import CircleTorunamentAvatar from "../../../utils/tournament/tournament_profile_img";
import { jwtDecode } from "jwt-decode";
import { useSelector } from "react-redux";
import { showToast } from "../../../components/ToastNotification";
import { FaPhoneAlt, FaCity, FaIdCard, FaBirthdayCake } from "react-icons/fa";

const PlayerDetailsTab = ({ player }) => {
  const [image, setImage] = useState(null);
  const token = useSelector((state) => state.user.token);
  const [userId, setUserId] = useState("");
  const [preview, setPreview] = useState(player.profile_picture_url || null);
  const [showUpdateButton, setShowUpdateButton] = useState(false);
  const isPlayerOwner = userId === player.user_id;

  useEffect(() => {
    if (token) {
      const decodedToken = jwtDecode(token);
      setUserId(decodedToken.id);
    }
  }, [token]);

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setImage(file);
      setShowUpdateButton(true);

      const reader = new FileReader();
      reader.onloadend = () => setPreview(reader.result);
      reader.readAsDataURL(file);
    }
  };

  const handleUpdate = async () => {
    if (!image) return;

    const formData = new FormData();
    formData.append("player_image", image);

    try {
      const response = await fetch(
        `https://server-postgre.vercel.app/api/players/${player.id}/image`,
        {
          method: "PUT",
          headers: { Authorization: `Bearer ${token}` },
          body: formData,
        }
      );

      const data = await response.json();
      if (!response.ok) throw new Error(data.message);

      showToast.success("Foto del jugador actualizada con éxito");
      setShowUpdateButton(false);
      setImage(null);
    } catch (error) {
      console.error(error);
      showToast.error("Error al actualizar la foto del jugador");
    }
  };

  return (
    <div className="max-w-3xl mx-auto bg-gray-50 rounded-2xl shadow-lg p-6 space-y-6">
      {/* Imagen del jugador */}
      <div className="flex justify-center">
        {isPlayerOwner ? (
          <CircleTorunamentAvatar
            size={140}
            imageSrc={preview}
            onImageSelect={handleImageChange}
            className="border-4 border-gradient-to-r from-purple-400 via-pink-500 to-red-500 shadow-xl"
          />
        ) : preview ? (
          <img
            src={preview}
            alt="Player"
            className="w-36 h-36 rounded-full object-cover border-4 border-emerald-400 shadow-md"
          />
        ) : (
          <div className="w-36 h-36 rounded-full flex items-center justify-center bg-gray-200 border-4 border-emerald-400 shadow-md">
            <FaIdCard size={70} color="#16a34a" />
          </div>
        )}
      </div>

      {/* Botón actualizar solo dueño */}
      {isPlayerOwner && showUpdateButton && (
        <div className="flex justify-center">
          <button
            onClick={handleUpdate}
            className="bg-gradient-to-r from-purple-500 to-pink-500 text-white px-6 py-2 rounded-full font-semibold shadow-md hover:scale-105 transform transition"
          >
            Actualizar Imagen
          </button>
        </div>
      )}

      {/* Nombre del jugador */}
      <h2 className="text-2xl font-bold text-gray-800 text-center">
        {player.first_name} {player.last_name}
      </h2>

      {/* Información del jugador */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="flex items-center p-3 bg-white rounded-xl shadow hover:shadow-lg transition">
          <FaPhoneAlt className="text-green-500 mr-3" />
          {player.phone ? (
            <a
              href={`tel:${player.phone}`}
              className="text-blue-600 hover:underline"
            >
              {player.phone}
            </a>
          ) : (
            <span>No especificado</span>
          )}
        </div>

        <div className="flex items-center p-3 bg-white rounded-xl shadow hover:shadow-lg transition">
          <FaCity className="text-emerald-500 mr-3" />
          <span>
            <strong>Ciudad:</strong> {player.city || "No especificado"}
          </span>
        </div>

        <div className="flex items-center p-3 bg-white rounded-xl shadow hover:shadow-lg transition">
          <FaIdCard className="text-blue-500 mr-3" />
          <span>
            <strong>Identificación:</strong>{" "}
            {player.identification || "No especificado"}
          </span>
        </div>

        <div className="flex items-center p-3 bg-white rounded-xl shadow hover:shadow-lg transition">
          <FaBirthdayCake className="text-pink-500 mr-3" />
          <span>
            <strong>Fecha de nacimiento:</strong>{" "}
            {player.birth_date
              ? new Date(player.birth_date).toLocaleDateString()
              : "No especificado"}
          </span>
        </div>

        <div className="flex items-center p-3 bg-white rounded-xl shadow hover:shadow-lg transition">
          <span>
            <strong>Tipo de jugador:</strong>{" "}
            {player.player_type || "No especificado"}
          </span>
        </div>

        {player.review && (
          <div className="col-span-1 sm:col-span-2 p-3 bg-white rounded-xl shadow hover:shadow-lg transition">
            <strong>Reseña:</strong> {player.review}
          </div>
        )}
      </div>
    </div>
  );
};

export default PlayerDetailsTab;
